import {
  ApolloLink,
  InMemoryCacheConfig,
  RequestHandler
} from '@do/graphql-client';
import { BadgeColor } from '@do/walrus';
import { Permission } from '../permissions';

export interface NavItem {
  customBadge?: 'action' | 'new' | 'soon';
  title: string;
  link?: string;
  local?: boolean;
  hidden?: boolean;
  isExternal?: boolean;
  badgeLabel?: string;
  badgeColor?: BadgeColor;
  active?: boolean;
  permissions: Permission[];
}

export interface TwoFactorItems {
  type: string;
  is_primary: boolean;
  phone_number: string;
  code_count: number;
}

export type PaymentMethods = 'cc' | 'paypal' | 'free';

export interface Organization {
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { team } = useCurrentUser();
   * team.id
   */
  id: number;
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { team } = useCurrentUser();
   * team.uuid
   */
  uuid: string;
  /** @deprecated fetch directly from graphql */
  avatar_initials: string;
  /** @deprecated fetch directly from graphql */
  avatar_color: number;
  /** @deprecated fetch directly from graphql */
  name: string;
  /** @deprecated fetch directly from graphql */
  contact_email: string;
  /** @deprecated fetch directly from graphql */
  role: string;
  /** @deprecated fetch directly from graphql */
  status: string;
  /** @deprecated fetch directly from graphql */
  requires_secure_login: boolean;
  /** @deprecated fetch directly from graphql */
  onboarding_step: string;
  /** @deprecated fetch directly from graphql */
  payment_method: PaymentMethods;
  /** @deprecated fetch directly from graphql */
  is_trial: boolean;
  /** @deprecated fetch directly from graphql */
  usage_total: string;
  /** @deprecated fetch directly from graphql */
  in_bad_standing: boolean;
  /** @deprecated fetch directly from graphql */
  is_ach_debit_eligible: boolean;
  /** @deprecated fetch directly from graphql */
  is_wire_transfer_eligible: boolean;
  /** @deprecated fetch directly from graphql */
  is_achwire_only: boolean;
  /** @deprecated fetch directly from graphql */
  is_hold: boolean;
  /** @deprecated fetch directly from graphql */
  credit?: string;
  /** @deprecated fetch directly from graphql */
  credits?: Credit[];
  /** @deprecated fetch directly from graphql */
  droplet_limit?: number;
  /** @deprecated fetch directly from graphql */
  droplet_count?: number;
  /** @deprecated fetch directly from graphql */
  pending_email: string | null;
  /** @deprecated fetch directly from graphql */
  email_verified_at: string | null;
  /** @deprecated fetch directly from graphql */
  payment_terms?: string;
  /** @deprecated fetch directly from graphql */
  subject_role?: {
    description: string;
    name: string;
  };
}

export interface Credit {
  credit_type: string;
  remaining_amount: string;
  expiration_date: string;
  description: string;
  issue_amount: string;
}

export interface BillingCredit {
  created_at: string;
  expires_on: string;
  description: string;
  initial_amount: string;
  remaining_amount: string;
}

export interface Account {
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { user } = useCurrentUser();
   * user.id
   */
  id: string;
  /** @deprecated fetch directly from graphql */
  avatar: string;
  /** @deprecated fetch directly from graphql */
  name: string;
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { user } = useCurrentUser();
   * user.email
   */
  email: string;
  /** @deprecated fetch directly from graphql */
  nav_message: string;
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { user } = useCurrentUser();
   * user.uuid
   */
  uuid: string;
  /** @deprecated fetch directly from graphql */
  created_at: string;
  /** @deprecated fetch directly from graphql */
  organizations: Organization[];
  /** @deprecated fetch directly from graphql */
  team_limit: number;
  /** @deprecated fetch directly from graphql */
  in_bad_standing: boolean;
  /** @deprecated fetch directly from graphql */
  status: string;
  /** @deprecated fetch directly from graphql */
  onboarding_step: string;
  /** @deprecated fetch directly from graphql */
  onboarding_origin: string;
  /** @deprecated fetch directly from graphql */
  is_hold: boolean;
  /** @deprecated fetch directly from graphql */
  is_trial: boolean;
  /** @deprecated fetch directly from graphql */
  usage_total: string;
  /** @deprecated fetch directly from graphql */
  credit: string;
  /** @deprecated fetch directly from graphql */
  credits: Credit[];
  /** @deprecated fetch directly from graphql */
  has_secure_login: boolean;
  /** @deprecated fetch directly from graphql */
  authn_type: string;
  /** @deprecated fetch directly from graphql */
  payment_method: PaymentMethods;
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { team } = useCurrentUser();
   * team?.id
   */
  current_context_id?: string;
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { team } = useCurrentUser();
   * team?.urn
   */
  current_context_urn?: string;
  /** @deprecated fetch directly from graphql */
  default_team_urn?: string;
  /** @deprecated fetch directly from graphql */
  tfa_enabled: boolean;
  /** @deprecated fetch directly from graphql */
  pending_login_email: string;
  /** @deprecated fetch directly from graphql */
  two_factor_methods: TwoFactorItems[];
  /** @deprecated fetch directly from graphql */
  has_vendor_portal_access?: boolean;
  /** @deprecated fetch directly from graphql */
  droplet_limit?: number;
  /** @deprecated fetch directly from graphql */
  droplet_count?: number;
  /** @deprecated fetch directly from graphql */
  payment_terms?: string;
  /** @deprecated fetch directly from graphql */
  role?: never;
}

interface BaseUser extends Account {
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { team } = useCurrentUser();
   * const isTeamContext = !!team;
   */
  is_team_context?: boolean;
  /** @deprecated fetch directly from graphql */
  features: string[];
  /** @deprecated fetch directly from graphql */
  can_access_billing: boolean;
  /** @deprecated fetch directly from graphql */
  is_read_only: boolean;
  /** @deprecated fetch directly from graphql */
  is_biller: boolean;
  /** @deprecated fetch directly from graphql */
  loading: boolean;
  /** @deprecated fetch directly from graphql */
  switchToContextId: string;
  /** @deprecated fetch directly from graphql */
  switchToContextLocation: string;
  /** @deprecated fetch directly from graphql */
  switchToUserContext?: boolean;
  /** @deprecated fetch directly from graphql */
  shouldSkipAccountTransitionAnimation?: boolean;
  /** @deprecated fetch directly from graphql */
  authn_type: string;
  /** @deprecated fetch directly from graphql */
  teamsv3_enabled: boolean;
  /** @deprecated fetch directly from graphql */
  can_create_teams: boolean;
  /** @deprecated fetch directly from graphql */
  is_vendor: boolean;
  /** @deprecated fetch directly from graphql */
  session_id?: string;
}

export interface OrganizationUser extends BaseUser {
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { team } = useCurrentUser();
   * const isTeamContext = !!team;
   */
  is_team_context: true;
  /** @deprecated fetch directly from graphql */
  current_context: Organization;
}

export interface IndividualUser extends BaseUser {
  /**
   * @deprecated replaced by '@do/current-user'
   *
   * @example
   * import { useCurrentUser } from '@do/current-user';
   * const { team } = useCurrentUser();
   * const isTeamContext = !!team;
   */
  is_team_context: false;
  /** @deprecated fetch directly from graphql */
  current_context: Account;
}

export type User = OrganizationUser | IndividualUser;

interface Header {
  [key: string]: string | string[] | null;
}
export interface ShellApolloClientOptions {
  headers?: Header;
  errStringsFor404?: string[];
  uri?: string;
  name?: string;
  version?: string;
  additionalLinks?: (ApolloLink | RequestHandler)[];
  inMemoryCacheConfig?: InMemoryCacheConfig;
}

export interface UserStanding {
  label:
    | 'unknown'
    | 'registered'
    | 'confirmed'
    | 'activated'
    | 'engaged'
    | 'established';
  conversionAction?:
    | 'signup_start'
    | 'confirmed'
    | 'activated'
    | 'engaged'
    | 'established';
}

export interface UserStandings {
  unknown: UserStanding;
  registered: UserStanding;
  confirmed: UserStanding;
  activated: UserStanding;
  engaged: UserStanding;
  established: UserStanding;
}

/**
 * @deprecated replaced by '@do/current-user'. Use team context directly.
 *
 * @example
 * import { useCurrentUser } from '@do/current-user';
 * const { team } = useCurrentUser();
 * const isOrganization = !!team;
 */
export function isOrganization(user: User): user is OrganizationUser {
  if (user.is_team_context) {
    return Array.isArray(user.organizations);
  }
  return false;
}
